// src/components/records/RecordsPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  useMediaQuery,
  useTheme,
  Paper,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Tabs,
  Tab,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  InputAdornment,
  Autocomplete,
  Pagination,
  Card,
  CardContent,
  LinearProgress,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  SwipeableDrawer,
  Fab,
  Stack,
  Badge,
  Drawer,
  AppBar,
  Toolbar,
  Menu,
  Container,
  CardHeader,
  CardActions
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Plus, 
  FileText, 
  Search, 
  Calendar,
  User,
  Clock,
  Edit2,
  Trash2,
  Heart,
  Activity,
  Thermometer,
  Droplet,
  Filter,
  Download,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle,
  BarChart2,
  MoreVertical,
  X,
  ArrowLeft,
  Clipboard,
  Menu as MenuIcon
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { HEALTH_GREEN } from '../../Login/utils/mockData';
import agthealthService from '../../../services/agthealthService';

// Importar sidebar y header compartidos
import Sidebar from '../../common/sidebar/Sidebar';
import DashboardHeader from '../../common/header/DashboardHeader';
import PatientModal from '../patients/PatientModal';

const DrawerWidth = 240;

const Main = styled(Box)(({ theme, open, isMobile }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  paddingTop: 0,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: isMobile ? 0 : `-${DrawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    paddingTop: 0,
  }
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiTabs-indicator': {
    backgroundColor: HEALTH_GREEN,
  },
  '& .MuiTab-root.Mui-selected': {
    color: HEALTH_GREEN,
    fontWeight: 600,
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2),
    '& .MuiTab-root': {
      minWidth: 'auto',
      padding: theme.spacing(1),
      fontSize: '0.85rem',
    }
  }
}));

const SearchContainer = styled(Paper)(({ theme, isMobile }) => ({
  padding: isMobile ? theme.spacing(1.5) : theme.spacing(2),
  marginBottom: isMobile ? theme.spacing(2) : theme.spacing(3),
  borderRadius: 12,
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  alignItems: isMobile ? 'stretch' : 'center',
  gap: theme.spacing(isMobile ? 1.5 : 2),
}));

const FiltersContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  marginBottom: theme.spacing(2),
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 12,
  marginBottom: theme.spacing(3),
  '& .MuiTable-root': {
    minWidth: 650,
  },
  '& .MuiTableHead-root': {
    backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#f5f5f7',
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
  },
}));

const StyledBadge = styled(Chip)(({ theme, color }) => ({
  backgroundColor: color ? `${color}20` : undefined,
  color: color,
  fontWeight: 500,
  borderRadius: 8,
}));

const StatCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  height: '100%',
  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
}));

const ActionButton = styled(Button)(({ theme, size }) => ({
  borderRadius: 12,
  padding: size === 'small' ? theme.spacing(0.5, 1.5) : theme.spacing(1, 2),
  textTransform: 'none',
  backgroundColor: HEALTH_GREEN,
  color: 'white',
  '&:hover': {
    backgroundColor: '#3d8b40',
  },
  fontWeight: 500,
}));

// Tarjeta de registro para vista móvil
const RecordCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)'
  }
}));

// Diálogo adaptado a móvil
const ResponsiveDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    margin: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      margin: 0,
      maxHeight: '100%',
      height: '100%',
      borderRadius: 0,
    }
  }
}));

// Botón flotante para móvil
const FloatingActionButton = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: 20,
  right: 20,
  backgroundColor: HEALTH_GREEN,
  color: 'white',
  '&:hover': {
    backgroundColor: '#3d8b40',
  },
  zIndex: 1000,
}));

const RecordsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [user, setUser] = useState({
    firstName: 'Dr.',
    lastName: 'Usuario',
    specialty: 'Profesional de la salud'
  });
  
  // Estado para las pestañas
  const [tabValue, setTabValue] = useState(0);
  
  // Estados para los registros
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  
  // Estados para la búsqueda y filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Estado para paginación
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    limit: isMobile ? 5 : 10
  });
  
  // Estado para estadísticas
  const [stats, setStats] = useState({
    totalRecords: 0,
    uniquePatients: 0,
    recordsPerPatient: {}
  });
  
  // Estado para el modal de nuevo registro
  const [recordDialog, setRecordDialog] = useState({
    open: false,
    recordId: null,
    patientId: null
  });
  
  // Estado para el modal de confirmación de eliminación
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    recordId: null
  });
  
  // Estado para el modal de paciente
  const [patientModal, setPatientModal] = useState({
    open: false,
    patientId: null
  });
  
  // Estado para el menú de acciones en móvil
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  
  // Estado para el formulario de registro
  const [formData, setFormData] = useState({
    patientId: '',
    date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    vitalSigns: {
      systolicBP: '',
      diastolicBP: '',
      heartRate: '',
      oxygenSaturation: '',
      temperature: '',
      bloodGlucose: '',
      weight: '',
      height: ''
    },
    notes: '',
    diagnosis: '',
    treatment: '',
    appointmentId: ''
  });
  
  // Ajustar límite de paginación según el tamaño de pantalla
  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      limit: isMobile ? 5 : 10
    }));
  }, [isMobile]);
  
  // Cargar datos iniciales
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Obtener el perfil del usuario
        const userResponse = await agthealthService.getUserProfile();
        if (userResponse.data) {
          setUser(userResponse.data);
        }
        
        // Obtener la lista de pacientes
        const patientsResponse = await agthealthService.getPatients();
        setPatients(patientsResponse.data || []);
        
        // Obtener estadísticas
        const statsResponse = await agthealthService.getAttendanceStats();
        if (statsResponse.data) {
          setStats(statsResponse.data);
        }
        
        // Cargar registros iniciales
        await fetchRecords();
        
        setError(null);
      } catch (err) {
        console.error("Error al cargar datos:", err);
        setError("No se pudieron cargar los datos");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Función para cargar registros con filtros
  const fetchRecords = async (page = 1) => {
    try {
      setLoading(true);
      
      // Preparar filtros
      const filters = {};
      
      if (selectedPatient) {
        filters.patientId = selectedPatient.patientId;
      }
      
      if (dateRange.startDate && dateRange.endDate) {
        filters.startDate = dateRange.startDate;
        filters.endDate = dateRange.endDate;
      }
      
      // Realizar búsqueda
      const response = await agthealthService.searchRecords(filters, page, pagination.limit);
      
      if (response.success) {
        setRecords(response.data || []);
        setPagination({
          currentPage: response.pagination.currentPage,
          totalPages: response.pagination.totalPages,
          limit: response.pagination.limit
        });
      } else {
        setError("Error al cargar registros");
      }
      
    } catch (err) {
      console.error("Error al cargar registros:", err);
      setError("No se pudieron cargar los registros");
    } finally {
      setLoading(false);
    }
  };
  
  // Función para obtener el nombre del paciente
  const getPatientName = (patientId) => {
    const patient = patients.find(p => p.patientId === patientId);
    return patient 
      ? `${patient.firstName} ${patient.lastName}`
      : 'Paciente no encontrado';
  };
  
  // Obtener registro seleccionado
  const getSelectedRecord = () => {
    return records.find(record => record.recordId === selectedRecordId) || null;
  };
  
  // Manejadores de eventos
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  const handleLogout = async () => {
    try {
      await agthealthService.logout();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };
  
  const handleSettings = () => {
    navigate('/settings');
  };
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
    fetchRecords(newPage);
  };
  
  const handlePatientChange = (event, newValue) => {
    setSelectedPatient(newValue);
    // Resetear paginación al cambiar de paciente
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  };
  
  const handleDateRangeChange = (event) => {
    const { name, value } = event.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSearch = () => {
    // Resetear paginación al buscar
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
    fetchRecords(1);
  };
  
  const handleClearFilters = () => {
    setSelectedPatient(null);
    setDateRange({
      startDate: '',
      endDate: ''
    });
    // Resetear paginación al limpiar filtros
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
    fetchRecords(1);
  };
  
  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
  };
  
  const handleNewRecord = (patientId = null) => {
    // Inicializar formulario para nuevo registro
    setFormData({
      patientId: patientId || '',
      date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      vitalSigns: {
        systolicBP: '',
        diastolicBP: '',
        heartRate: '',
        oxygenSaturation: '',
        temperature: '',
        bloodGlucose: '',
        weight: '',
        height: ''
      },
      notes: '',
      diagnosis: '',
      treatment: '',
      appointmentId: ''
    });
    
    setRecordDialog({
      open: true,
      recordId: null,
      patientId: patientId
    });
  };
  
  const handleEditRecord = (record) => {
    // Inicializar formulario con datos del registro existente
    setFormData({
      patientId: record.patientId,
      date: format(parseISO(record.date), "yyyy-MM-dd'T'HH:mm"),
      vitalSigns: {
        systolicBP: record.vitalSigns?.systolicBP || '',
        diastolicBP: record.vitalSigns?.diastolicBP || '',
        heartRate: record.vitalSigns?.heartRate || '',
        oxygenSaturation: record.vitalSigns?.oxygenSaturation || '',
        temperature: record.vitalSigns?.temperature || '',
        bloodGlucose: record.vitalSigns?.bloodGlucose || '',
        weight: record.vitalSigns?.weight || '',
        height: record.vitalSigns?.height || ''
      },
      notes: record.notes || '',
      diagnosis: record.diagnosis || '',
      treatment: record.treatment || '',
      appointmentId: record.appointmentId || ''
    });
    
    setRecordDialog({
      open: true,
      recordId: record.recordId,
      patientId: record.patientId
    });
  };
  
  const handleDeleteClick = (recordId) => {
    setDeleteDialog({
      open: true,
      recordId
    });
  };
  
  const handleDeleteConfirm = async () => {
    if (!deleteDialog.recordId) return;
    
    try {
      setLoading(true);
      await agthealthService.deleteRecord(deleteDialog.recordId);
      
      // Cerrar diálogo y recargar registros
      setDeleteDialog({ open: false, recordId: null });
      fetchRecords(pagination.currentPage);
      
      // Actualizar estadísticas
      const statsResponse = await agthealthService.getAttendanceStats();
      if (statsResponse.data) {
        setStats(statsResponse.data);
      }
    } catch (err) {
      console.error("Error al eliminar registro:", err);
      setError("No se pudo eliminar el registro");
    } finally {
      setLoading(false);
    }
  };
  
  const handleCloseDialog = () => {
    setRecordDialog({ open: false, recordId: null, patientId: null });
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleVitalSignChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      vitalSigns: {
        ...prev.vitalSigns,
        [name]: value
      }
    }));
  };
  
  const handleSaveRecord = async () => {
    try {
      setLoading(true);
      
      // Validar campos obligatorios
      if (!formData.patientId) {
        setError("El paciente es obligatorio");
        setLoading(false);
        return;
      }
      
      const recordData = {
        ...formData,
        date: new Date(formData.date).toISOString()
      };
      
      if (recordDialog.recordId) {
        // Actualizar registro existente
        await agthealthService.updateRecord(recordDialog.recordId, recordData);
      } else {
        // Crear nuevo registro
        await agthealthService.createRecord(recordData);
      }
      
      // Cerrar diálogo y recargar registros
      setRecordDialog({ open: false, recordId: null, patientId: null });
      fetchRecords(pagination.currentPage);
      
      // Actualizar estadísticas
      const statsResponse = await agthealthService.getAttendanceStats();
      if (statsResponse.data) {
        setStats(statsResponse.data);
      }
      
    } catch (err) {
      console.error("Error al guardar registro:", err);
      setError("No se pudo guardar el registro");
    } finally {
      setLoading(false);
    }
  };
  
  const handleNewPatient = () => {
    setPatientModal({
      open: true,
      patientId: null
    });
  };
  
  const handleClosePatientModal = () => {
    setPatientModal({
      ...patientModal,
      open: false
    });
  };
  
  const handlePatientSaved = async () => {
    // Recargar la lista de pacientes
    try {
      const patientsResponse = await agthealthService.getPatients();
      setPatients(patientsResponse.data || []);
    } catch (err) {
      console.error("Error al recargar pacientes:", err);
    }
  };
  
  // Manejo del menú de acciones para móvil
  const handleOpenActionMenu = (recordId) => {
    setSelectedRecordId(recordId);
    setActionMenuOpen(true);
  };
  
  const handleCloseActionMenu = () => {
    setActionMenuOpen(false);
    setSelectedRecordId(null);
  };
  
  // Renderizar la pestaña de registros
  const renderRecordsTab = () => (
    <>
      {/* Contenedor de búsqueda adaptado a móvil */}
      <SearchContainer elevation={0} isMobile={isMobile}>
        <Box sx={{ width: '100%' }}>
          <Autocomplete
            options={patients}
            getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
            value={selectedPatient}
            onChange={handlePatientChange}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Filtrar por paciente"
                placeholder="Todos los pacientes"
                fullWidth
                size={isSmall ? "small" : "medium"}
              />
            )}
            sx={{ width: '100%' }}
          />
        </Box>

        {/* Botón para mostrar/ocultar filtros avanzados en móvil */}
        {isMobile && (
          <Button
            variant="outlined"
            onClick={handleToggleFilters}
            startIcon={showFilters ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            fullWidth
            sx={{
              borderColor: HEALTH_GREEN,
              color: HEALTH_GREEN,
              '&:hover': {
                borderColor: HEALTH_GREEN,
                bgcolor: 'rgba(76, 175, 80, 0.1)'
              },
              borderRadius: 2
            }}
          >
            {showFilters ? "Ocultar filtros" : "Mostrar filtros"}
          </Button>
        )}

        {/* Filtros de fecha que se muestran/ocultan en móvil */}
        <Collapse in={!isMobile || showFilters} sx={{ width: '100%' }}>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            mt: isMobile ? 1.5 : 0,
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            <TextField
              label="Fecha inicio"
              type="date"
              name="startDate"
              value={dateRange.startDate}
              onChange={handleDateRangeChange}
              InputLabelProps={{ shrink: true }}
              fullWidth={isMobile}
              size={isSmall ? "small" : "medium"}
              sx={{ width: isMobile ? '100%' : 180 }}
            />
            
            <TextField
              label="Fecha fin"
              type="date"
              name="endDate"
              value={dateRange.endDate}
              onChange={handleDateRangeChange}
              InputLabelProps={{ shrink: true }}
              fullWidth={isMobile}
              size={isSmall ? "small" : "medium"}
              sx={{ width: isMobile ? '100%' : 180 }}
            />
          </Box>
        </Collapse>
        
        {/* Botones de búsqueda y limpiar */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          width: '100%', 
          flexDirection: isMobile ? 'row' : 'row',
          mt: isMobile ? 1 : 0
        }}>
          <Button
            variant="contained"
            onClick={handleSearch}
            startIcon={<Search size={18} />}
            fullWidth={isMobile}
            size={isSmall ? "small" : "medium"}
            sx={{ 
              bgcolor: HEALTH_GREEN,
              '&:hover': { bgcolor: '#3d8b40' },
              borderRadius: 2,
              px: isMobile ? 2 : 3,
              py: isMobile ? 0.75 : 1,
              flex: 1
            }}
          >
            {isSmall ? "Buscar" : "Buscar registros"}
          </Button>
          
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            size={isSmall ? "small" : "medium"}
            sx={{ 
              borderColor: HEALTH_GREEN,
              color: HEALTH_GREEN,
              '&:hover': { 
                borderColor: HEALTH_GREEN,
                bgcolor: 'rgba(76, 175, 80, 0.1)' 
              },
              borderRadius: 2,
              flex: isMobile ? 1 : 'none'
            }}
          >
            Limpiar
          </Button>
        </Box>
      </SearchContainer>
      
      {/* Encabezado y botones de acción */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        flexWrap: 'wrap',
        gap: 1
      }}>
        <Typography 
          variant={isSmall ? "subtitle1" : "h6"} 
          sx={{ 
            fontWeight: 600,
            fontSize: isSmall ? '1rem' : undefined
          }}
        >
          {selectedPatient 
            ? `Registros de ${selectedPatient.firstName} ${selectedPatient.lastName}`
            : 'Todos los registros'
          }
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {selectedPatient && !isSmall && (
            <Button 
              startIcon={<BarChart2 size={18} />}
              onClick={() => navigate(`/records/trends/${selectedPatient.patientId}`)}
              sx={{ 
                borderRadius: 2,
                borderColor: HEALTH_GREEN,
                color: HEALTH_GREEN,
                '&:hover': { 
                  borderColor: HEALTH_GREEN,
                  bgcolor: 'rgba(76, 175, 80, 0.1)' 
                }
              }}
              variant="outlined"
              size={isSmall ? "small" : "medium"}
            >
              Ver Tendencias
            </Button>
          )}
          
          {!isMobile && (
            <ActionButton 
              startIcon={<Plus size={18} />}
              onClick={() => handleNewRecord(selectedPatient?.patientId)}
              size={isSmall ? "small" : "medium"}
            >
              Nuevo Registro
            </ActionButton>
          )}
        </Box>
      </Box>
      
      {loading && records.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress sx={{ color: HEALTH_GREEN }} />
        </Box>
      ) : records.length === 0 ? (
        <Alert 
          severity="info" 
          sx={{ 
            borderRadius: 2,
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            color: HEALTH_GREEN
          }}
        >
          No se encontraron registros con los filtros actuales.
        </Alert>
      ) : (
        <>
          {/* Vista de escritorio: Tabla */}
          {!isMobile && (
            <StyledTableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Paciente</TableCell>
                    <TableCell>Signos vitales</TableCell>
                    <TableCell>Diagnóstico</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.map((record) => (
                    <TableRow key={record.recordId}>
                      <TableCell>
                        {format(parseISO(record.date), "dd MMM yyyy, HH:mm", { locale: es })}
                      </TableCell>
                      <TableCell>{getPatientName(record.patientId)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {record.vitalSigns?.systolicBP && record.vitalSigns?.diastolicBP && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Activity size={14} style={{ marginRight: 4, color: HEALTH_GREEN }} />
                              <Typography variant="body2">
                                {record.vitalSigns.systolicBP}/{record.vitalSigns.diastolicBP} mmHg
                              </Typography>
                            </Box>
                          )}
                          {record.vitalSigns?.heartRate && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Heart size={14} style={{ marginRight: 4, color: HEALTH_GREEN }} />
                              <Typography variant="body2">
                                {record.vitalSigns.heartRate} lpm
                              </Typography>
                            </Box>
                          )}
                          {record.vitalSigns?.bloodGlucose && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Droplet size={14} style={{ marginRight: 4, color: HEALTH_GREEN }} />
                              <Typography variant="body2">
                                {record.vitalSigns.bloodGlucose} mg/dL
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {record.diagnosis ? (
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {record.diagnosis}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Sin diagnóstico
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Editar registro">
                          <IconButton 
                            onClick={() => handleEditRecord(record)}
                            size="small"
                            sx={{ color: HEALTH_GREEN }}
                          >
                            <Edit2 size={18} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar registro">
                          <IconButton 
                            onClick={() => handleDeleteClick(record.recordId)}
                            size="small"
                            color="error"
                          >
                            <Trash2 size={18} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
          
          {/* Vista móvil: Tarjetas */}
          {isMobile && (
            <Box sx={{ mb: 2 }}>
              {records.map((record) => (
                <RecordCard key={record.recordId}>
                  <CardContent sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {getPatientName(record.patientId)}
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenActionMenu(record.recordId)}
                        sx={{ padding: '4px', marginRight: -1 }}
                      >
                        <MoreVertical size={18} />
                      </IconButton>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Calendar size={14} style={{ marginRight: 6, color: '#666' }} />
                      <Typography variant="body2" color="text.secondary">
                        {format(parseISO(record.date), "dd MMM yyyy, HH:mm", { locale: es })}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <Grid container spacing={1} sx={{ mt: 0.5 }}>
                      {record.vitalSigns?.systolicBP && record.vitalSigns?.diastolicBP && (
                        <Grid item xs={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Activity size={14} style={{ marginRight: 4, color: HEALTH_GREEN }} />
                            <Typography variant="body2">
                              {record.vitalSigns.systolicBP}/{record.vitalSigns.diastolicBP} mmHg
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      
                      {record.vitalSigns?.heartRate && (
                        <Grid item xs={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Heart size={14} style={{ marginRight: 4, color: HEALTH_GREEN }} />
                            <Typography variant="body2">
                              {record.vitalSigns.heartRate} lpm
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      
                      {record.vitalSigns?.temperature && (
                        <Grid item xs={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Thermometer size={14} style={{ marginRight: 4, color: HEALTH_GREEN }} />
                            <Typography variant="body2">
                              {record.vitalSigns.temperature} °C
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      
                      {record.vitalSigns?.bloodGlucose && (
                        <Grid item xs={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Droplet size={14} style={{ marginRight: 4, color: HEALTH_GREEN }} />
                            <Typography variant="body2">
                              {record.vitalSigns.bloodGlucose} mg/dL
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                    
                    {record.diagnosis && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Diagnóstico:
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {record.diagnosis.length > 100 ? 
                            record.diagnosis.substring(0, 100) + "..." : 
                            record.diagnosis
                          }
                        </Typography>
                      </>
                    )}
                  </CardContent>
                  
                  <CardActions sx={{ pt: 0, pb: 1, px: 2 }}>
                    <Button
                      size="small"
                      startIcon={<Edit2 size={16} />}
                      onClick={() => handleEditRecord(record)}
                      sx={{ color: HEALTH_GREEN }}
                    >
                      Editar
                    </Button>
                    <Button
                      size="small"
                      startIcon={<Trash2 size={16} />}
                      onClick={() => handleDeleteClick(record.recordId)}
                      sx={{ color: '#f44336' }}
                    >
                      Eliminar
                    </Button>
                  </CardActions>
                </RecordCard>
              ))}
            </Box>
          )}
          
          {/* Paginación adaptada */}
          {pagination.totalPages > 1 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mt: 2,
              '& .MuiPagination-ul': {
                flexWrap: isSmall ? 'nowrap' : 'wrap'
              }
            }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                size={isSmall ? "small" : "medium"}
                siblingCount={isSmall ? 0 : 1}
                boundaryCount={isSmall ? 1 : 2}
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: HEALTH_GREEN,
                  },
                  '& .Mui-selected': {
                    backgroundColor: `${HEALTH_GREEN}!important`,
                    color: 'white',
                  }
                }}
              />
            </Box>
          )}
        </>
      )}
      
      {/* Botón flotante para crear nuevo registro (solo en móvil) */}
      {isMobile && (
        <FloatingActionButton 
          color="primary" 
          aria-label="nuevo registro" 
          onClick={() => handleNewRecord(selectedPatient?.patientId)}
        >
          <Plus />
        </FloatingActionButton>
      )}
    </>
  );
  
  // Renderizar la pestaña de estadísticas
  const renderStatsTab = () => (
    <>
      <Grid container spacing={isMobile ? 2 : 3}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard>
            <CardContent>
              <Typography variant={isSmall ? "subtitle1" : "h6"} sx={{ fontWeight: 600, mb: 1 }}>
                Total de Registros
              </Typography>
              <Typography variant={isSmall ? "h4" : "h3"} sx={{ color: HEALTH_GREEN, fontWeight: 600 }}>
                {stats.totalRecords}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Registros médicos guardados
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <StatCard>
            <CardContent>
              <Typography variant={isSmall ? "subtitle1" : "h6"} sx={{ fontWeight: 600, mb: 1 }}>
                Pacientes Atendidos
              </Typography>
              <Typography variant={isSmall ? "h4" : "h3"} sx={{ color: HEALTH_GREEN, fontWeight: 600 }}>
                {stats.uniquePatients}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Pacientes con al menos un registro
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <StatCard>
            <CardContent>
              <Typography variant={isSmall ? "subtitle1" : "h6"} sx={{ fontWeight: 600, mb: 1 }}>
                Promedio de Visitas
              </Typography>
              <Typography variant={isSmall ? "h4" : "h3"} sx={{ color: HEALTH_GREEN, fontWeight: 600 }}>
                {stats.uniquePatients ? (stats.totalRecords / stats.uniquePatients).toFixed(1) : '0'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Registros promedio por paciente
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
        
        <Grid item xs={12}>
          <StatCard>
            <CardContent>
              <Typography variant={isSmall ? "subtitle1" : "h6"} sx={{ fontWeight: 600, mb: 3 }}>
                Registros por Paciente
              </Typography>
              
              {Object.keys(stats.recordsPerPatient).length === 0 ? (
                <Alert severity="info">No hay datos de registros disponibles.</Alert>
              ) : (
                <Box>
                  {Object.entries(stats.recordsPerPatient).slice(0, 10).map(([patientId, count]) => (
                    <Box key={patientId} sx={{ mb: 2 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        mb: 0.5,
                        flexWrap: 'wrap'
                      }}>
                        <Typography variant="body2" sx={{ maxWidth: '70%' }}>
                          {getPatientName(patientId)}
                        </Typography>
                        <Typography variant="body2" fontWeight="500">
                          {count} registros
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.min((count / stats.totalRecords) * 100, 100)} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          bgcolor: 'rgba(76, 175, 80, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: HEALTH_GREEN
                          }
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </StatCard>
        </Grid>
      </Grid>
    </>
  );
  
  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar compartido */}
      <Sidebar
        open={drawerOpen}
        isMobile={isMobile}
        onClose={handleDrawerToggle}
        onNewPatient={handleNewPatient}
        onLogout={handleLogout}
        activeSection="records"
      />
      
      {/* Main Content */}
      <Main open={drawerOpen} isMobile={isMobile}>
        <DashboardHeader 
          title="Registros Médicos"
          user={user}
          isMobile={isMobile}
          onMenuClick={handleDrawerToggle}
          onLogout={handleLogout}
          onSettings={handleSettings}
        />
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {/* Pestañas */}
        <StyledTabs 
          value={tabValue} 
          onChange={handleTabChange}
          variant={isSmall ? "fullWidth" : "standard"}
        >
          <Tab 
            label={isSmall ? "Registros" : "Registros"} 
            icon={isSmall ? <FileText size={16} /> : <FileText size={18} />} 
            iconPosition="start"
          />
          <Tab 
            label={isSmall ? "Estadísticas" : "Estadísticas"} 
            icon={isSmall ? <BarChart2 size={16} /> : <BarChart2 size={18} />} 
            iconPosition="start"
          />
        </StyledTabs>
        
        {/* Contenido según la pestaña seleccionada */}
        {tabValue === 0 && renderRecordsTab()}
        {tabValue === 1 && renderStatsTab()}
        
        {/* Diálogo para crear/editar registro */}
        <ResponsiveDialog 
          open={recordDialog.open} 
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          fullScreen={isSmall}
        >
          {/* Barra superior especial para móvil */}
          {isSmall && (
            <AppBar position="static" color="transparent" elevation={0} sx={{ borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
              <Toolbar>
                <IconButton edge="start" color="inherit" onClick={handleCloseDialog}>
                  <ArrowLeft />
                </IconButton>
                <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>
                  {recordDialog.recordId ? 'Editar Registro' : 'Nuevo Registro'}
                </Typography>
                <Button 
                  color="primary"
                  onClick={handleSaveRecord}
                  disabled={loading}
                >
                  Guardar
                </Button>
              </Toolbar>
            </AppBar>
          )}
          
          {/* Título solo para desktop */}
          {!isSmall && (
            <DialogTitle sx={{ pb: 1 }}>
              {recordDialog.recordId ? 'Editar Registro' : 'Nuevo Registro'}
            </DialogTitle>
          )}
          
          <DialogContent sx={{ pb: isSmall ? 8 : 4 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <Grid container spacing={2} sx={{ mt: isSmall ? 2 : 0.5 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="patient-label">Paciente *</InputLabel>
                  <Select
                    labelId="patient-label"
                    name="patientId"
                    value={formData.patientId}
                    onChange={handleInputChange}
                    label="Paciente *"
                    disabled={loading || recordDialog.patientId}
                    size={isSmall ? "small" : "medium"}
                  >
                    {patients.map(patient => (
                      <MenuItem key={patient.patientId} value={patient.patientId}>
                        {patient.firstName} {patient.lastName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Fecha y Hora *"
                  name="date"
                  type="datetime-local"
                  value={formData.date}
                  onChange={handleInputChange}
                  disabled={loading}
                  InputLabelProps={{ shrink: true }}
                  size={isSmall ? "small" : "medium"}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Signos Vitales
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Presión Sistólica"
                  name="systolicBP"
                  type="number"
                  value={formData.vitalSigns.systolicBP}
                  onChange={handleVitalSignChange}
                  disabled={loading}
                  size={isSmall ? "small" : "medium"}
                  InputProps={{ 
                    startAdornment: (
                      <InputAdornment position="start">
                        <Activity size={isSmall ? 16 : 18} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Presión Diastólica"
                  name="diastolicBP"
                  type="number"
                  value={formData.vitalSigns.diastolicBP}
                  onChange={handleVitalSignChange}
                  disabled={loading}
                  size={isSmall ? "small" : "medium"}
                  InputProps={{ 
                    startAdornment: (
                      <InputAdornment position="start">
                        <Activity size={isSmall ? 16 : 18} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Frecuencia Cardíaca"
                  name="heartRate"
                  type="number"
                  value={formData.vitalSigns.heartRate}
                  onChange={handleVitalSignChange}
                  disabled={loading}
                  size={isSmall ? "small" : "medium"}
                  InputProps={{ 
                    startAdornment: (
                      <InputAdornment position="start">
                        <Heart size={isSmall ? 16 : 18} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Saturación Oxígeno"
                  name="oxygenSaturation"
                  type="number"
                  value={formData.vitalSigns.oxygenSaturation}
                  onChange={handleVitalSignChange}
                  disabled={loading}
                  size={isSmall ? "small" : "medium"}
                  InputProps={{ 
                    startAdornment: (
                      <InputAdornment position="start">
                        <Activity size={isSmall ? 16 : 18} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Temperatura (°C)"
                  name="temperature"
                  type="number"
                  value={formData.vitalSigns.temperature}
                  onChange={handleVitalSignChange}
                  disabled={loading}
                  size={isSmall ? "small" : "medium"}
                  InputProps={{ 
                    startAdornment: (
                      <InputAdornment position="start">
                        <Thermometer size={isSmall ? 16 : 18} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Glucosa en Sangre"
                  name="bloodGlucose"
                  type="number"
                  value={formData.vitalSigns.bloodGlucose}
                  onChange={handleVitalSignChange}
                  disabled={loading}
                  size={isSmall ? "small" : "medium"}
                  InputProps={{ 
                    startAdornment: (
                      <InputAdornment position="start">
                        <Droplet size={isSmall ? 16 : 18} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Peso (kg)"
                  name="weight"
                  type="number"
                  value={formData.vitalSigns.weight}
                  onChange={handleVitalSignChange}
                  disabled={loading}
                  size={isSmall ? "small" : "medium"}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Altura (cm)"
                  name="height"
                  type="number"
                  value={formData.vitalSigns.height}
                  onChange={handleVitalSignChange}
                  disabled={loading}
                  size={isSmall ? "small" : "medium"}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, mt: 1 }}>
                  Información Clínica
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Diagnóstico"
                  name="diagnosis"
                  value={formData.diagnosis}
                  onChange={handleInputChange}
                  disabled={loading}
                  multiline
                  rows={isSmall ? 2 : 2}
                  size={isSmall ? "small" : "medium"}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Tratamiento"
                  name="treatment"
                  value={formData.treatment}
                  onChange={handleInputChange}
                  disabled={loading}
                  multiline
                  rows={isSmall ? 2 : 2}
                  size={isSmall ? "small" : "medium"}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notas adicionales"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  disabled={loading}
                  multiline
                  rows={isSmall ? 2 : 3}
                  size={isSmall ? "small" : "medium"}
                />
              </Grid>
            </Grid>
          </DialogContent>
          
          {/* Botones de acción solo para desktop */}
          {!isSmall && (
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button 
                onClick={handleCloseDialog}
                disabled={loading}
                sx={{ borderRadius: 2 }}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveRecord}
                variant="contained"
                disabled={loading}
                sx={{ 
                  borderRadius: 2,
                  bgcolor: HEALTH_GREEN,
                  '&:hover': { bgcolor: '#3d8b40' }
                }}
              >
                {loading ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogActions>
          )}
          
          {/* Botón flotante para guardar en móvil */}
          {isSmall && !recordDialog.open && (
            <Fab
              color="primary"
              sx={{
                position: 'fixed',
                bottom: 16,
                right: 16,
                bgcolor: HEALTH_GREEN,
                '&:hover': { bgcolor: '#3d8b40' }
              }}
              onClick={handleSaveRecord}
              disabled={loading}
            >
              <CheckCircle />
            </Fab>
          )}
        </ResponsiveDialog>
        
        {/* Diálogo de confirmación de eliminación */}
        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, recordId: null })}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: isSmall ? 0 : 3,
              padding: 1,
              margin: isSmall ? 0 : undefined,
              width: isSmall ? '100%' : undefined,
              height: isSmall ? 'auto' : undefined,
            }
          }}
          fullScreen={isSmall}
        >
          <DialogTitle>Confirmar eliminación</DialogTitle>
          <DialogContent>
            <Typography>
              ¿Estás seguro de que deseas eliminar este registro? Esta acción no se puede deshacer.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={() => setDeleteDialog({ open: false, recordId: null })}
              disabled={loading}
              sx={{ borderRadius: 2 }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleDeleteConfirm}
              variant="contained"
              color="error"
              disabled={loading}
              sx={{ borderRadius: 2 }}
            >
              {loading ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Modal de paciente */}
        <PatientModal
          open={patientModal.open}
          onClose={handleClosePatientModal}
          patientId={patientModal.patientId}
          onSaved={handlePatientSaved}
          fullScreen={isSmall}
        />
        
        {/* Menú de acciones para móvil (bottom drawer) */}
        {isMobile && (
          <SwipeableDrawer
            anchor="bottom"
            open={actionMenuOpen}
            onClose={handleCloseActionMenu}
            onOpen={() => {}}
            PaperProps={{
              sx: {
                borderTopLeftRadius: '16px',
                borderTopRightRadius: '16px',
                maxWidth: '100%',
                width: '100%',
              },
            }}
          >
            <Box sx={{ py: 1 }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  textAlign: 'center', 
                  fontWeight: 600,
                  py: 1,
                  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                  position: 'relative'
                }}
              >
                Acciones
                <IconButton
                  onClick={handleCloseActionMenu}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)'
                  }}
                >
                  <X size={18} />
                </IconButton>
              </Typography>
              
              <List sx={{ pt: 0 }}>
                {getSelectedRecord() && (
                  <>
                    <ListItem 
                      button 
                      onClick={() => {
                        const record = getSelectedRecord();
                        handleEditRecord(record);
                        handleCloseActionMenu();
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Edit2 size={20} color={HEALTH_GREEN} />
                      </ListItemIcon>
                      <ListItemText primary="Editar registro" />
                    </ListItem>
                    
                    {selectedPatient && (
                      <ListItem 
                        button 
                        onClick={() => {
                          navigate(`/records/trends/${selectedPatient.patientId}`);
                          handleCloseActionMenu();
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <BarChart2 size={20} color={HEALTH_GREEN} />
                        </ListItemIcon>
                        <ListItemText primary="Ver tendencias" />
                      </ListItem>
                    )}
                    
                    <ListItem 
                      button 
                      onClick={() => {
                        const record = getSelectedRecord();
                        handleDeleteClick(record.recordId);
                        handleCloseActionMenu();
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Trash2 size={20} color="#f44336" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Eliminar registro" 
                        primaryTypographyProps={{ sx: { color: '#f44336' } }}
                      />
                    </ListItem>
                  </>
                )}
              </List>
            </Box>
          </SwipeableDrawer>
        )}
      </Main>
    </Box>
  );
};

export default RecordsPage;